import { onBeforeUnmount, reactive } from 'vue';
import { getSurveyResourcePreviewUrl } from './SurveyStatistics.access';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'svg'];
const RESOURCE_PREVIEW_CONCURRENCY = 4;

export function isImageFile(fileName: string): boolean {
    const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
    return IMAGE_EXTENSIONS.includes(extension);
}

// Object URLs for image previews of uploaded survey answer files, keyed by ResourceId.
// Shared by the survey statistics response detail and the event guest list response modal.
export function useSurveyResourcePreviews() {
    const resourcePreviewUrls = reactive<Record<string, string>>({});

    function clearResourcePreviewUrls() {
        Object.values(resourcePreviewUrls).forEach((url) => window.URL.revokeObjectURL(url));
        Object.keys(resourcePreviewUrls).forEach((key) => delete resourcePreviewUrls[key]);
    }

    async function loadResourcePreviews(resources: { ResourceId: string; OriginalFileName: string }[]) {
        const images = resources.filter((resource) => isImageFile(resource.OriginalFileName));

        let nextIndex = 0;
        async function worker() {
            while (nextIndex < images.length) {
                const resource = images[nextIndex++];
                const url = await getSurveyResourcePreviewUrl(resource.ResourceId, resource.OriginalFileName);
                resourcePreviewUrls[resource.ResourceId] = url;
            }
        }

        await Promise.all(Array.from({ length: Math.min(RESOURCE_PREVIEW_CONCURRENCY, images.length) }, worker));
    }

    onBeforeUnmount(clearResourcePreviewUrls);

    return { resourcePreviewUrls, clearResourcePreviewUrls, loadResourcePreviews };
}
